import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useLanguage } from "../../i18n/LanguageContext";

type CurriculumCard = {
  title: string;
  description: string;
  image: string;
  imagePosition?: string;
};

const defaultCards: CurriculumCard[] = [
  {
    title: "National System (Arabic / Languages Track)",
    description:
      "Best for students who want full integration into the Egyptian school system. Taught in Arabic, with a language specialization option.",
    image: "/images/quilia-1-aA2Fadydc-unsplash.jpg",
    imagePosition: "left center",
  },
  {
    title: "British IG System",
    description:
      "One of the most widely recognized international curricula. Students study 8 subjects over 2-3 years and can sit for exams more than once to improve their grades. Accepted by universities worldwide.",
    image: "/images/quilia-1-aA2Fadydc-unsplash.jpg",
    imagePosition: "center center",
  },
  {
    title: "American Diploma",
    description:
      "A credit-hour based system where students progress at their own pace. Globally recognized, including the SAT, which remains one of the best-known pathways into higher education.",
    image: "/images/quilia-1-aA2Fadydc-unsplash.jpg",
    imagePosition: "right center",
  },
];

const CurriculumPathsSection: React.FC = () => {
  const { t, dir } = useLanguage();
  const sectionRef = useRef<HTMLElement | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  const isRtl = dir === "rtl";

  const content = (t as any)?.homeCurriculumPaths || {};
  const cards = ((content.items as CurriculumCard[]) || defaultCards).slice(0, 3);

  useEffect(() => {
    const element = sectionRef.current;
    if (!element) return;

    if (!("IntersectionObserver" in window)) {
      setIsVisible(true);
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { rootMargin: "0px 0px -15% 0px", threshold: 0.2 }
    );

    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  return (
    <section
      ref={sectionRef}
      className="overflow-hidden bg-white py-24 sm:py-28"
      aria-labelledby="curriculum-paths-title"
    >
      <div className="site-container flex flex-col gap-10">
        <div
          className={`mx-auto flex w-full max-w-4xl flex-col items-center gap-2 text-center transition-all duration-700 ease-out ${
            isVisible ? "translate-y-0 opacity-100" : "translate-y-8 opacity-0"
          }`}
        >
          <h2
            id="curriculum-paths-title"
            className="header-title capitalize text-teal-900"
          >
            <span className="text-main-color">
              {content.titleHighlight || "Three"}
            </span>{" "}
            {content.title ||
              "curriculum paths. We'll help you choose the right one."}
          </h2>
        </div>

        <div className="-mx-5 flex snap-x snap-mandatory items-stretch gap-4 overflow-x-auto px-5 pb-2 sm:-mx-8 sm:px-8 lg:mx-0 lg:grid lg:grid-cols-3 lg:gap-6 lg:overflow-visible lg:px-0 lg:pb-0">
          {cards.map((card, index) => (
            <article
              key={card.title}
              className={`group relative h-[80vh] max-h-[80vh] w-[82vw] max-w-[22rem] shrink-0 snap-center overflow-hidden transition-all duration-700 ease-out hover:-translate-y-1 hover:shadow-xl sm:w-[70vw] sm:max-w-[24rem] lg:h-[80vh] lg:max-h-[80vh] lg:w-full lg:max-w-none ${
                isVisible ? "translate-y-0 opacity-100" : "translate-y-10 opacity-0"
              }`}
              style={{ transitionDelay: `${index * 100}ms` }}
            >
              <div
                className="absolute inset-0 bg-cover bg-center transition-transform duration-700 group-hover:scale-105"
                style={{
                  backgroundImage: `url('${card.image}')`,
                  backgroundPosition: card.imagePosition || "center center",
                }}
                aria-hidden="true"
              />
              <div className="absolute inset-0 bg-black/25" aria-hidden="true" />
              <div
                className={`absolute inset-0 ${
                  index === 1
                    ? "bg-gradient-to-t from-black/90 via-black/65 to-black/15"
                    : "bg-gradient-to-t from-black/90 via-black/60 to-black/10"
                }`}
                aria-hidden="true"
              />

              <div
                className={`relative z-10 flex h-full min-h-0 flex-col justify-end p-6 sm:p-7 ${
                  isRtl ? "items-end text-right" : "items-start text-left"
                }`}
              >
                <div className="flex max-w-[18rem] flex-col gap-3">
                  <h3 className="font-sans text-xl font-semibold leading-6 text-white">
                    {card.title}
                  </h3>
                  <p className="font-sans text-sm font-normal leading-6 text-white/90 sm:text-base">
                    {card.description}
                  </p>
                </div>
              </div>
            </article>
          ))}
        </div>

        <div
          className={`flex flex-col gap-5 transition-all duration-700 ease-out lg:flex-row lg:items-center lg:justify-between ${
            isVisible ? "translate-y-0 opacity-100" : "translate-y-8 opacity-0"
          } ${isRtl ? "lg:flex-row-reverse" : ""}`}
          style={{ transitionDelay: "220ms" }}
        >
          <p
            className={`section-subtitle max-w-[466px] text-neutral-600 ${
              isRtl ? "text-right" : "text-left"
            }`}
          >
            {content.footerText ||
              "Not sure which fits your child? Our advisors will help you figure it out."}
          </p>

          <Link
            to="/contact"
            data-property-1="prim"
            className="inline-flex min-h-10 w-full items-center justify-center gap-2.5 bg-second-color px-6 py-2 text-center font-sans text-base font-medium leading-6 text-main-color transition-colors hover:bg-second-color/90 focus:outline-none focus:ring-2 focus:ring-main-color focus:ring-offset-2 focus:ring-offset-white sm:w-fit"
          >
            {content.cta || "Get a Free Curriculum Consultation"}
          </Link>
        </div>
      </div>
    </section>
  );
};

export default CurriculumPathsSection;
